import React, { useRef, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { GroupRowColumns } from 'src/components/common/rows/GroupRow';
import moment from 'moment';
import { weekdays, eventSlugnameTrim } from 'src/components/helpers';
import AddGroupMember from './components/addMember';
import { Button, Icon } from 'semantic-ui-react';
import ViewCoupleGroup from 'src/containers/groups/view-couple-group';
import ViewGroupMembers from './components/viewGroupMembers';
import PendingNewMembers from './components/pendingNewMembers';
import PendingWeddingRequestMembersOnGroup from './components/pendingWeddingRequestMembersOnGroup';
import SetSeminaryGroupAttendance from 'src/containers/crecer/seminaries/setSeminaryGroupAttendance';
import AGTable from 'src/components/table/aggrid';
import { screenSizes } from 'src/assets/styles/Literals';

const GroupViewShell = styled.div`
  width: 100%;
  margin: 0 auto;
  text-align: center;
  padding: 0.25rem 0 0.5rem;
  box-sizing: border-box;
`;

const GroupHeaderCard = styled.div`
  width: 100%;
  background: #f3f9fc;
  border: 1px solid #d8ecf5;
  border-radius: 16px;
  padding: 1.35rem 1.5rem 1.5rem;
  margin: 0.75rem 0 1.25rem;
  text-align: center;
  box-sizing: border-box;

  @media only screen and (max-width: ${screenSizes.mobile}) {
    padding: 1rem 0.9rem 1.15rem;
    border-radius: 12px;
  }

  .group-name {
    margin: 0 0 0.55rem;
    font-size: 1.45rem;
    font-weight: 700;
    color: #1a1a1a;
    line-height: 1.25;

    @media only screen and (max-width: ${screenSizes.mobile}) {
      font-size: 1.2rem;
    }
  }

  .activity-badge {
    display: inline-block;
    background: #fff;
    color: #1a6f8f;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    padding: 0.4rem 0.85rem;
    border-radius: 999px;
    border: 1px solid #c5e8f5;
    margin: 0 0 0.9rem;
  }

  .schedule {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.65rem 1.25rem;
    color: #444;
    font-size: 0.95rem;

    span {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
    }

    strong {
      color: #1a1a1a;
    }

    i.icon {
      color: #4db8e8;
      margin: 0 !important;
    }
  }
`;

const ActionsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  margin: 0 0 1.25rem;

  .ui.button {
    margin: 0 !important;
  }
`;

const StatusMessage = styled.p`
  margin: 0 auto 1rem;
  max-width: 36rem;
  color: #555;
  line-height: 1.55;
  font-size: 0.95rem;
`;

const TableWrap = styled.div`
  width: 100%;
  text-align: left;
  margin-top: 0.5rem;
  box-sizing: border-box;

  .ag-theme-alpine,
  .ag-root-wrapper,
  .ag-root {
    width: 100% !important;
  }
`;

const GroupView = (props) => {
  const { inputs, loading, kindgrouplist, group } = props;
  const [openNewMemberModal, setOpenNewMemberModal] = React.useState(false);
  const [openMembers, setOpenMembers] = React.useState(false);
  const [openEditGroupModal, setOpenEditGroupModal] = React.useState(false);
  const [openPendingNewMembersModal, setOpenPendingNewMembersModal] = React.useState(false);
  const gridRef = useRef(null);
  const gridApiRef = useRef(null);

  const isCouples = !!(
    (inputs.activityName && inputs.activityName.includes('Parejas')) ||
    inputs.coupleleadered
  );

  const activeMembers = group.GroupMembers?.filter((member) => !member.pending) || [];
  const pendingMembers = group.GroupMembers?.filter((member) => member.pending);

  const getInfo = useCallback(() => props.getInfo(), [props.getInfo]);
  const isSeminary = group?.kind === 'Grupo_Seminario';

  const fitColumns = useCallback(() => {
    const api = gridApiRef.current || gridRef.current?.api;
    if (api) api.sizeColumnsToFit();
  }, []);

  const onGridReady = useCallback((params) => {
    gridApiRef.current = params.api;
    params.api.sizeColumnsToFit();
  }, []);

  useEffect(() => {
    fitColumns();
  }, [activeMembers, fitColumns]);

  useEffect(() => {
    window.addEventListener('resize', fitColumns);
    return () => window.removeEventListener('resize', fitColumns);
  }, [fitColumns]);

  const columnDefs = React.useMemo(
    () =>
      GroupRowColumns({
        isCouples,
        getInfo,
        takeattendance: group.takeattendance,
      }).map((col) => ({
        ...col,
        flex: col.flex ?? 1,
        minWidth: col.minWidth || col.width || 120,
        width: undefined,
      })),
    [isCouples, group.takeattendance, getInfo]
  );

  return (
    <GroupViewShell>
      <GroupHeaderCard>
        {group.groupName && <h3 className="group-name">{group.groupName}</h3>}
        <span className="activity-badge">{eventSlugnameTrim(group.activityName)}</span>
        <div className="schedule">
          <span>
            <Icon name="calendar outline" />
            <strong>Día:</strong> {weekdays[group.dayOfWeek]}
          </span>
          <span>
            <Icon name="clock outline" />
            <strong>Hora:</strong> {moment(group.time, 'HH:mm').format('hh:mm a z')}
          </span>
        </div>
      </GroupHeaderCard>

      <ActionsBar>
        <Button onClick={() => setOpenNewMemberModal(true)}>
          <Icon name="user plus" /> Agregar participante
        </Button>
        {isSeminary ? (
          <Button primary onClick={() => setOpenMembers(true)}>
            <Icon name="check square outline" /> Ver y marcar asistencia
          </Button>
        ) : (
          <Button onClick={() => setOpenMembers(true)}>
            <Icon name="users" /> Ver info. de miembros
          </Button>
        )}
        <ViewCoupleGroup
          getInfo={getInfo}
          open={openEditGroupModal}
          setOpen={setOpenEditGroupModal}
          editing
          editingInputs={group}
          isCouples={isCouples}
          activeMembers={activeMembers}
          match={props.match}
          kindgrouplist={kindgrouplist}
        />
        {!!pendingMembers?.length && (
          <Button color="black" onClick={() => setOpenPendingNewMembersModal(true)}>
            <Icon name="inbox" /> Solicitudes ({pendingMembers.length})
          </Button>
        )}
        <PendingWeddingRequestMembersOnGroup group_id={group.id} />
      </ActionsBar>

      {openNewMemberModal && (
        <AddGroupMember
          setOpen={setOpenNewMemberModal}
          group={group}
          isCouples={isCouples}
          getInfo={getInfo}
        />
      )}
      {openMembers &&
        (isSeminary ? (
          <SetSeminaryGroupAttendance
            group={group}
            setOpen={setOpenMembers}
            getInfo={getInfo}
            activeMembers={activeMembers}
          />
        ) : (
          <ViewGroupMembers
            setOpen={setOpenMembers}
            group={group}
            groupsLeaders={inputs.groupsLeaders}
            isCouples={isCouples}
            getInfo={getInfo}
            activeMembers={activeMembers}
          />
        ))}
      {openPendingNewMembersModal && (
        <PendingNewMembers
          group={group}
          activeMembers={activeMembers}
          setOpen={setOpenPendingNewMembersModal}
          pendingMembers={pendingMembers}
          isCouples={isCouples}
          getInfo={getInfo}
        />
      )}

      {inputs.groups && group && activeMembers.length === 0 && !loading && (
        <StatusMessage>
          Tu grupo ya quedó registrado. Te invitamos a que registres los miembros de tu grupo.
        </StatusMessage>
      )}
      {loading && <StatusMessage>Cargando…</StatusMessage>}

      {activeMembers.length > 0 && (
        <TableWrap>
          <AGTable
            rowData={activeMembers}
            rowHeight={50}
            suppressDragLeaveHidesColumns={false}
            columnDefs={columnDefs}
            tableRef={gridRef}
            onGridReady={onGridReady}
          />
        </TableWrap>
      )}
    </GroupViewShell>
  );
};

export default GroupView;
